import { makeAutoObservable, runInAction } from 'mobx';
import { injectable } from 'inversify';
import { ChatApiRestFallback } from './chatApi';
import {
  type ChatMessageType,
  type ChatStatus,
  type MessageSender,
  type PageContext,
  type QuickReply,
  type ServerMessage,
} from './chatTypes';

// OSI-814-fix-bot: единый ключ сообщения для сравнения id из POST /chat/message
// (message_id) и из /chat/poll (id). Бэкенд отдаёт оба как id::text, но тип
// приводим к строке явно, чтобы Set/сравнение не разошлись на number vs string.
// null/undefined/'' — id нет (старый бэкенд, DRY_RUN, реплика не записана).
const toMessageKey = (id: unknown): string | null => (id === null || id === undefined || id === '' ? null : String(id));

@injectable()
export class ChatWidgetStore {
  isOpen = false;
  messages: ChatMessageType[] = [];
  sessionId: string | null = null;
  isConnected = false;
  isTyping = false;
  typingSender: MessageSender = 'bot';
  quickReplies: QuickReply[] = [];
  status: ChatStatus = 'disconnected';
  unreadCount = 0;
  buttonsQuestion: string | null = null;
  // Системный prompt «Вопрос решён?» — показывается, когда последнее сообщение от оператора
  showResolvePrompt = false;

  private restApi: ChatApiRestFallback | null = null;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;

  // OSI-814-fix-bot: ответ бота, ожидающий окончания «печатает» (typingTimer).
  // Нужен, чтобы при перезапуске таймера не потерять предыдущий ответ: его id уже
  // в shownMessageIds, и poll его больше не покажет.
  private pendingBotMessage: ServerMessage | null = null;

  private shownMessageIds = new Set<string>();

  constructor() {
    makeAutoObservable(this);
    this.restoreSession();
  }

  private restoreSession(): void {
    const savedSessionId = localStorage.getItem('chat_session_id');
    const savedMessages = sessionStorage.getItem('chat_messages');
    if (savedSessionId) {
      this.sessionId = savedSessionId;
    }
    if (savedMessages) {
      try {
        this.messages = (JSON.parse(savedMessages) as Array<ChatMessageType & { timestamp: string }>).map((m) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
      } catch {
        /* ignore */
      }
    }
  }

  private persistMessages(): void {
    sessionStorage.setItem('chat_messages', JSON.stringify(this.messages));
  }

  private persistSessionId(): void {
    if (this.sessionId) {
      localStorage.setItem('chat_session_id', this.sessionId);
    }
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.unreadCount = 0;
    }
  }

  setOpen(value: boolean): void {
    this.isOpen = value;
    if (value) this.unreadCount = 0;
  }

  initConnection(context: PageContext, clientId?: string): void {
    if (this.restApi) return;

    this.status = 'connecting';
    this.restApi = new ChatApiRestFallback();

    const existingSessionId = this.sessionId; // restored from localStorage

    if (existingSessionId) {
      // Восстанавливаем существующую сессию
      this.restApi.resumeSession(existingSessionId)
        .then((data) => {
          runInAction(() => {
            if (data.type === 'session_expired') {
              // Сессия истекла — начать новую
              this.sessionId = null;
              localStorage.removeItem('chat_session_id');
              this.messages = [];
              sessionStorage.removeItem('chat_messages');
              this.startNewSession(context, clientId);
            } else {
              // Восстановить сессию
              this.sessionId = existingSessionId;
              this.restApi!.setSessionId(existingSessionId);
              this.isConnected = true;
              // Восстановить статус
              if (data.status === 'with_operator') {
                this.status = 'with_operator';
              } else if (data.status === 'waiting_operator') {
                this.status = 'waiting_operator';
              } else if (data.status === 'resolved') {
                this.status = 'resolved';
              } else {
                this.status = 'bot';
              }
              // Восстановить сообщения из БД (НЕ дублировать — заменить)
              if (data.messages && data.messages.length > 0) {
                this.messages = data.messages.map((m: any) => ({
                  id: m.id || crypto.randomUUID(),
                  sender: m.sender as MessageSender,
                  text: m.text,
                  timestamp: new Date(m.created_at || Date.now()),
                }));
                // Загрузить ID в shownMessageIds
                for (const m of data.messages) {
                  if (m.id) this.shownMessageIds.add(m.id);
                }
                this.persistMessages();
              }
              // Начать polling — с текущего момента, чтобы не дублировать восстановленные сообщения
              this.restApi!.setLastTimestamp(data.server_time || new Date().toISOString());
              this.startPolling();
            }
          });
        })
        .catch(() => {
          runInAction(() => {
            // Ошибка resume — начать новую сессию
            this.startNewSession(context, clientId);
          });
        });
    } else {
      this.startNewSession(context, clientId);
    }
  }

  private startNewSession(context: PageContext, clientId?: string): void {
    this.restApi!.startSession(context, clientId)
      .then((data) => {
        runInAction(() => {
          if (data.session_id) {
            this.sessionId = data.session_id;
            this.persistSessionId();
          }
          this.isConnected = true;
          this.handleServerMessage(data);
          this.startPolling();
        });
      })
      .catch(() => {
        runInAction(() => {
          this.status = 'disconnected';
          this.isConnected = false;
        });
      });
  }

  private startPolling(): void {
    this.restApi?.startPolling((msgs, status, operatorName) => {
      runInAction(() => {
        // Обновить статус сессии
        if (status === 'with_operator' && this.status !== 'with_operator') {
          this.status = 'with_operator';
          if (operatorName) {
            this.addMessage('system', `Оператор ${operatorName} подключился к диалогу`);
          }
        } else if (status === 'resolved' && this.status !== 'resolved') {
          this.status = 'resolved';
          this.addMessage('system', 'Вопрос решён');
          this.quickReplies = [];
          this.showResolvePrompt = false;
        }

        // Добавить новые сообщения — dedup по ID + текст
        for (const msg of msgs) {
          // OSI-814-fix-bot: id приводим к строке — тот же ключ, что у message_id
          // из POST /chat/message (см. handleServerMessage/addBotMessage)
          const msgId = toMessageKey(msg.id);
          if (!msg.sender || msg.sender === 'client' || !msgId) continue;
          if (this.shownMessageIds.has(msgId)) continue;
          // Проверить дубль по тексту+sender (REST-ответы старого бэкенда не имеют DB id)
          const alreadyShown = this.messages.some(
            (m) => m.sender === msg.sender && m.text === msg.text
          );
          this.shownMessageIds.add(msgId);
          if (!alreadyShown) {
            // OSI-814-fix-bot: сообщение сохраняем с id строки БД, чтобы addBotMessage
            // мог понять, что poll уже показал этот ответ, и не добавить копию
            this.addMessage(msg.sender as MessageSender, msg.text || '', msgId);
          }
        }

        // Prompt «Вопрос решён?» — если последнее сообщение от оператора и идёт диалог с оператором
        const lastMsg = this.messages[this.messages.length - 1];
        if (lastMsg && lastMsg.sender === 'operator' && this.status === 'with_operator') {
          this.showResolvePrompt = true;
        }
      });
    });
  }

  sendMessage(text: string): void {
    if (!text.trim() || !this.restApi) return;

    // Пользователь отвечает текстом — prompt «Вопрос решён?» убираем
    this.showResolvePrompt = false;

    const msg: ChatMessageType = {
      id: crypto.randomUUID(),
      sender: 'client',
      text: text.trim(),
      timestamp: new Date(),
    };

    this.messages.push(msg);
    this.persistMessages();

    this.restApi.sendMessage(text.trim()).then((data) => {
      runInAction(() => {
        this.handleServerMessage(data);
      });
    }).catch(() => {
      // silently ignore
    });
  }

  confirmResolved(): void {
    // «Да» — пользователь подтвердил решение, закрываем чат
    this.showResolvePrompt = false;
    this.restApi?.resolveSession().then(() => {
      runInAction(() => {
        this.status = 'resolved';
        this.addMessage('system', 'Вопрос решён');
        this.quickReplies = [];
      });
    }).catch(() => { /* silently ignore */ });
  }

  declineResolved(): void {
    // «Нет» — отправляем как обычное сообщение оператору, чат остаётся открытым
    // sendMessage сам сбросит showResolvePrompt в false
    this.sendMessage('Нет');
  }

  handleServerMessage(data: ServerMessage): void {
    runInAction(() => {
      switch (data.type) {
        case 'bot_response':
        case 'buttons':
        case 'error': {
          // OSI-814-fix-bot: бэкенд записывает ответ в chat_messages ДО ответа на POST,
          // поэтому poll может вернуть его, пока идёт «печатает». Регистрируем id
          // сразу (до таймера) — poll пропустит это сообщение, и копии не будет.
          const botMessageId = toMessageKey(data.message_id);
          if (botMessageId) this.shownMessageIds.add(botMessageId);
          // OSI-814-fix-bot: если poll успел показать ответ раньше HTTP-ответа,
          // «печатает» уже не нужно — сразу довешиваем кнопки (addBotMessage не дублирует).
          const alreadyInMessages = !!botMessageId && this.messages.some((m) => m.id === botMessageId);
          if (data.typing_duration && data.typing_duration > 0 && !alreadyInMessages) {
            this.isTyping = true;
            this.typingSender = 'bot';
            if (this.typingTimer) {
              clearTimeout(this.typingTimer);
              // OSI-814-fix-bot: предыдущий ответ ещё «печатался» — показываем его сразу,
              // иначе он потеряется (poll его пропустит: id уже в shownMessageIds)
              if (this.pendingBotMessage) this.addBotMessage(this.pendingBotMessage);
            }
            this.pendingBotMessage = data;
            this.typingTimer = setTimeout(() => {
              runInAction(() => {
                this.isTyping = false;
                this.typingTimer = null;
                this.pendingBotMessage = null;
                this.addBotMessage(data);
              });
            }, Math.min(data.typing_duration * 1000, 3000));
          } else {
            this.addBotMessage(data);
          }
          break;
        }

        case 'escalation':
          this.addMessage('bot', data.text || 'Подключаем оператора...');
          this.status = 'waiting_operator';
          this.quickReplies = [];
          break;

        case 'operator_joined':
          this.addMessage('system', `Оператор ${data.name || ''} подключился к диалогу`);
          this.status = 'with_operator';
          this.quickReplies = [];
          break;

        case 'operator_message':
          this.addMessage('operator', data.text || '');
          // После ответа оператора предлагаем подтвердить решение вопроса
          if (this.status === 'with_operator') {
            this.showResolvePrompt = true;
          }
          break;

        case 'resolved':
          this.addMessage('system', data.text || 'Вопрос решён');
          this.status = 'resolved';
          this.quickReplies = [];
          this.showResolvePrompt = false;
          break;

        case 'forwarded':
          // Confirmation of forwarding — don't show as separate message
          break;

        case 'session_started':
          if (data.session_id) {
            this.sessionId = data.session_id;
            this.persistSessionId();
          }
          break;
      }
    });
  }

  private addBotMessage(data: ServerMessage): void {
    // OSI-814-fix-bot: если poll уже добавил это сообщение (тот же id строки БД) —
    // второй раз не добавляем, только выставляем кнопки/вопрос ниже.
    // Без message_id (старый бэкенд) — как раньше: добавляем со случайным id.
    const botMessageId = toMessageKey(data.message_id);
    const alreadyShown = !!botMessageId && this.messages.some((m) => m.id === botMessageId);
    if (!alreadyShown) {
      this.addMessage('bot', data.text || '', botMessageId ?? undefined);
    }
    this.quickReplies = data.quick_replies || [];
    this.buttonsQuestion = data.buttons_question || null;
    if (data.escalated) {
      this.status = 'waiting_operator';
    } else if (this.status === 'disconnected' || this.status === 'connecting') {
      this.status = 'bot';
    }
  }

  // OSI-814-fix-bot: необязательный id — id строки chat_messages (для дедупликации
  // ответа бота между POST /chat/message и /chat/poll); без него — случайный UUID, как раньше
  private addMessage(sender: MessageSender, text: string, id?: string): void {
    if (!text) return;
    this.messages.push({
      id: id ?? crypto.randomUUID(),
      sender,
      text,
      timestamp: new Date(),
    });
    this.persistMessages();
    if (!this.isOpen) {
      this.unreadCount++;
    }
  }

  clearSession(): void {
    this.sessionId = null;
    this.messages = [];
    this.status = 'disconnected';
    this.quickReplies = [];
    this.unreadCount = 0;
    this.buttonsQuestion = null;
    this.showResolvePrompt = false;
    this.isTyping = false;
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
    this.pendingBotMessage = null; // OSI-814-fix-bot: ожидающий ответ относится к старой сессии
    this.shownMessageIds.clear();
    this.restApi?.stopPolling();
    this.restApi = null;
    localStorage.removeItem('chat_session_id');
    sessionStorage.removeItem('chat_messages');
  }
}
